import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from './api';

export type Role = 'owner' | 'admin' | 'user';

export interface Session {
  id: string;
  username: string;
  name: string;
  /** Effective role — what the interface should render and the API enforces. */
  role: Role;
  /** The role the account really holds. Unaffected by previewing. */
  actualRole: Role;
  /** Set while previewing a lower role, null when acting normally. */
  viewingAs: Role | null;
  /** Roles this account may preview. Derived from actualRole, so it never vanishes. */
  canViewAs: Role[];
  /** Null means every store; a list means exactly those. */
  storeIds: string[] | null;
  capabilities: {
    manageUsers: boolean;
    manageAnyStore: boolean;
    manageAssignedStores: boolean;
    viewStatistics: boolean;
    resetDatabase: boolean;
  };
}

export function useSession() {
  return useQuery<Session | null>({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        return await apiFetch<Session>('/api/me');
      } catch (error) {
        // Signed out is a normal state, not an error worth retrying or surfacing.
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useSignIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { username: string; password: string }) =>
      apiFetch<{ token?: string }>('/api/auth/sign-in/username', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useSignOut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch('/api/auth/sign-out', { method: 'POST', body: '{}' }),
    onSuccess: () => {
      queryClient.clear();
      window.location.assign('/');
    },
    /*
     * A failed sign-out is dangerous precisely because it is invisible: the
     * button appears to do nothing and the person walks away from a browser that
     * is still authenticated. Surfacing it is the whole point.
     */
    onError: (error: Error) => {
      window.alert(`Could not sign out: ${error.message}\n\nYou are still signed in.`);
    },
  });
}
